<script setup lang="ts">
import { Plus, X } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    MAX_ENTITY_TAGS,
    MAX_ENTITY_TAG_LENGTH,
    normalizeEntityTags,
    normalizeTagLabel,
    tagKey,
} from '~/lib/entityTags';

const props = withDefaults(defineProps<{
    modelValue?: string[];
    suggestions?: string[];
    placeholder?: string;
    addLabel?: string;
    suggestionsLabel?: string;
    emptyStateLabel?: string;
    maxTags?: number;
    maxLength?: number;
    disabled?: boolean;
    loading?: boolean;
}>(), {
    modelValue: () => [],
    suggestions: () => [],
    placeholder: 'Add a tag',
    addLabel: 'Add tag',
    suggestionsLabel: 'Suggested tags',
    emptyStateLabel: 'Type a tag and press Enter.',
    maxTags: MAX_ENTITY_TAGS,
    maxLength: MAX_ENTITY_TAG_LENGTH,
    disabled: false,
    loading: false,
});

const emit = defineEmits<{
    'update:modelValue': [tags: string[]];
    add: [tag: string];
    remove: [tag: string];
}>();

const inputValue = ref('');
const tags = computed(() => normalizeEntityTags(props.modelValue, props.maxTags));
const canAddMore = computed(() => !props.disabled && !props.loading && tags.value.length < props.maxTags);
const candidate = computed(() => normalizeTagLabel(inputValue.value).slice(0, props.maxLength));
const candidateExists = computed(() => tags.value.some(tag => tagKey(tag) === tagKey(candidate.value)));
const filteredSuggestions = computed(() => {
    const query = tagKey(inputValue.value);
    return normalizeEntityTags(props.suggestions, Number.MAX_SAFE_INTEGER)
        .filter(tag => !tags.value.some(selected => tagKey(selected) === tagKey(tag)))
        .filter(tag => !query || tagKey(tag).includes(query))
        .slice(0, 8);
});

function addTag(value = candidate.value) {
    const tag = normalizeTagLabel(value).slice(0, props.maxLength);
    if (!tag || !canAddMore.value || tags.value.some(current => tagKey(current) === tagKey(tag))) return;
    emit('update:modelValue', [...tags.value, tag]);
    emit('add', tag);
    inputValue.value = '';
}

function removeTag(tag: string) {
    if (props.disabled || props.loading) return;
    emit('update:modelValue', tags.value.filter(value => tagKey(value) !== tagKey(tag)));
    emit('remove', tag);
}

function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        addTag();
        return;
    }
    if (event.key === 'Backspace' && !inputValue.value && tags.value.length) {
        removeTag(tags.value[tags.value.length - 1]!);
    }
}
</script>

<template>
    <div class="space-y-3">
        <div v-if="tags.length" class="flex flex-wrap gap-2">
            <Badge v-for="tag in tags" :key="tag" variant="secondary" class="gap-1.5 py-1 pl-2.5 pr-1.5 text-xs">
                <span class="max-w-48 truncate">{{ tag }}</span>
                <button
                    type="button"
                    class="inline-flex size-4 items-center justify-center rounded-full text-muted-foreground hover:bg-background/70 hover:text-foreground"
                    :aria-label="`Remove ${tag}`"
                    :disabled="disabled || loading"
                    @click="removeTag(tag)"
                >
                    <X class="size-3" />
                </button>
            </Badge>
        </div>

        <div class="flex gap-2">
            <Input
                v-model="inputValue"
                :placeholder="canAddMore ? placeholder : `Maximum ${maxTags} tags`"
                :maxlength="maxLength"
                :disabled="!canAddMore"
                @keydown="handleKeydown"
            />
            <Button
                type="button"
                variant="outline"
                size="icon"
                :aria-label="addLabel"
                :title="addLabel"
                :disabled="!candidate || candidateExists || !canAddMore"
                @click="addTag()"
            >
                <Plus class="size-4" />
            </Button>
        </div>

        <div v-if="filteredSuggestions.length" class="space-y-2">
            <p class="text-xs text-muted-foreground">{{ suggestionsLabel }}</p>
            <div class="flex flex-wrap gap-2">
                <Button
                    v-for="suggestion in filteredSuggestions"
                    :key="suggestion"
                    type="button"
                    variant="outline"
                    size="sm"
                    class="h-7 rounded-full px-2.5 text-xs"
                    :disabled="!canAddMore"
                    @click="addTag(suggestion)"
                >
                    <Plus class="size-3" />
                    {{ suggestion }}
                </Button>
            </div>
        </div>
        <p v-else-if="!tags.length && !inputValue" class="text-xs text-muted-foreground">{{ emptyStateLabel }}</p>
    </div>
</template>
